import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceMetrics = ({ metrics }) => {
  const getPerformanceColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getPerformanceIcon = (score) => {
    if (score >= 80) return 'TrendingUp';
    if (score >= 60) return 'Minus';
    return 'TrendingDown';
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="BarChart3" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-semibold text-foreground">Performance Metrics</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Code Quality */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-primary/20 rounded-full flex items-center justify-center">
            <Icon name="Code" size={24} color="var(--color-primary)" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${getPerformanceColor(metrics?.codeQuality)}`}>
            {metrics?.codeQuality}%
          </div>
          <div className="text-sm text-muted-foreground mb-2">Code Quality</div>
          <div className="flex items-center justify-center gap-1">
            <Icon 
              name={getPerformanceIcon(metrics?.codeQuality)} 
              size={16} 
              color={getPerformanceColor(metrics?.codeQuality)?.replace('text-', 'var(--color-')}
            />
            <span className={`text-xs ${getPerformanceColor(metrics?.codeQuality)}`}>
              {metrics?.codeQuality >= 80 ? 'Excellent' : 
               metrics?.codeQuality >= 60 ? 'Good' : 'Needs Work'}
            </span>
          </div>
        </div>

        {/* Execution Speed */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-accent/20 rounded-full flex items-center justify-center">
            <Icon name="Zap" size={24} color="var(--color-accent)" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${getPerformanceColor(metrics?.executionSpeed)}`}>
            {metrics?.executionSpeed}%
          </div>
          <div className="text-sm text-muted-foreground mb-2">Execution Speed</div>
          <div className="flex items-center justify-center gap-1">
            <Icon 
              name={getPerformanceIcon(metrics?.executionSpeed)} 
              size={16} 
              color={getPerformanceColor(metrics?.executionSpeed)?.replace('text-', 'var(--color-')}
            />
            <span className={`text-xs ${getPerformanceColor(metrics?.executionSpeed)}`}>
              {metrics?.actualTime}s
            </span>
          </div>
        </div>

        {/* Test Coverage */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-secondary/20 rounded-full flex items-center justify-center">
            <Icon name="Shield" size={24} color="var(--color-secondary)" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${getPerformanceColor(metrics?.testCoverage)}`}>
            {metrics?.testCoverage}%
          </div>
          <div className="text-sm text-muted-foreground mb-2">Test Coverage</div>
          <div className="flex items-center justify-center gap-1">
            <Icon 
              name={getPerformanceIcon(metrics?.testCoverage)} 
              size={16} 
              color={getPerformanceColor(metrics?.testCoverage)?.replace('text-', 'var(--color-')}
            />
            <span className={`text-xs ${getPerformanceColor(metrics?.testCoverage)}`}>
              {metrics?.testsRun}/{metrics?.totalTests} tests
            </span>
          </div>
        </div>

        {/* Best Practices */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-warning/20 rounded-full flex items-center justify-center">
            <Icon name="Award" size={24} color="var(--color-warning)" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${getPerformanceColor(metrics?.bestPractices)}`}>
            {metrics?.bestPractices}%
          </div>
          <div className="text-sm text-muted-foreground mb-2">Best Practices</div>
          <div className="flex items-center justify-center gap-1">
            <Icon 
              name={getPerformanceIcon(metrics?.bestPractices)} 
              size={16} 
              color={getPerformanceColor(metrics?.bestPractices)?.replace('text-', 'var(--color-')}
            />
            <span className={`text-xs ${getPerformanceColor(metrics?.bestPractices)}`}>
              {metrics?.practicesFollowed}/{metrics?.totalPractices} followed
            </span>
          </div>
        </div>
      </div>
      {/* Overall Performance Bar */}
      <div className="mt-6 pt-6 border-t border-subtle">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Overall Performance</span>
          <span className={`text-sm font-bold ${getPerformanceColor(metrics?.overallScore)}`}>
            {metrics?.overallScore}%
          </span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-700 ${
              metrics?.overallScore >= 80 ? 'bg-success' :
              metrics?.overallScore >= 60 ? 'bg-warning': 'bg-error'
            }`}
            style={{ width: `${metrics?.overallScore}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;